import { useMemo, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Moon, Sunrise, Sunset, Wind, Droplets, Eye, Thermometer, Flame, AlertTriangle, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fmtTemp, fmtWind, fmtDistance, popPct, dayLabel, iconForCondition, fmtClock, timeAgo, fToC } from "../lib/weather";
import { useI18n } from "../lib/i18n";

const ICONS = { Sun, Cloud, CloudRain, CloudSnow, CloudFog, CloudLightning, Moon };

function buildTips(c, forecast, t) {
  const tips = [];
  if (!c) return tips;
  const tF = c.temp_f;
  const today = forecast?.[0];
  const tomorrow = forecast?.[1];
  const popToday = (today?.pop || 0) * 100;
  const popTomorrow = (tomorrow?.pop || 0) * 100;
  if (tF != null && tF > 95) {
    tips.push({ kind: "amber", icon: Flame, text: t("weather_tip_hot") });
  }
  if (popToday > 50 || popTomorrow > 50) {
    tips.push({ kind: "amber", icon: CloudRain, text: t("weather_tip_rain") });
  }
  if (c.uv_index != null && c.uv_index > 7) {
    tips.push({ kind: "amber", icon: AlertTriangle, text: t("weather_tip_uv") });
  }
  if (tF != null && tF < 68) {
    tips.push({ kind: "info", icon: Moon, text: t("weather_tip_cool") });
  }
  if (tF != null && tF >= 68 && tF <= 86 && popToday < 30 && (c.uv_index == null || c.uv_index <= 7)) {
    tips.push({ kind: "gold", icon: Sparkles, text: t("weather_tip_perfect") });
  }
  return tips.slice(0, 2);
}

/**
 * Pick 4 forecast slots ~6 hours apart starting from "now".
 *
 * OpenWeatherMap free-tier returns 3-hour buckets; we take the first 8 and
 * pick indexes [0, 2, 4, 6] which approximate [now, +6h, +12h, +18h].
 */
function pickIntervalSlots(hourly) {
  if (!Array.isArray(hourly) || hourly.length === 0) return [];
  const idxs = [0, 2, 4, 6];
  return idxs.map((i) => hourly[i] || null);
}

/**
 * Build a one-line planning tip from the hourly forecast, focused on the
 * evening window (4–9 PM local). Returns null when forecast data is missing.
 */
function buildPlanningTip(hourly, unit, t, lang) {
  if (!Array.isArray(hourly) || hourly.length === 0) return null;
  const slotTime = (h) => {
    if (!h?.dt_unix && !h?.dt) return null;
    const ts = h.dt_unix ? h.dt_unix * 1000 : Date.parse((h.dt || "").replace(" ", "T") + "Z");
    return Number.isNaN(ts) ? null : new Date(ts);
  };

  // Find rainy stretch (pop > 0.5 across consecutive slots)
  const rainSlots = hourly.filter((h) => (h.pop || 0) > 0.5).map((h) => slotTime(h)).filter(Boolean);
  if (rainSlots.length >= 1) {
    const start = rainSlots[0];
    const end = rainSlots[rainSlots.length - 1];
    const fmt = (d) => d.toLocaleTimeString(lang === "es" ? "es-ES" : "en-US", { hour: "numeric", hour12: true, timeZone: "America/Havana" });
    return lang === "es"
      ? `Lluvia esperada de ${fmt(start)} a ${fmt(end)} — planea visitas en interiores esta tarde.`
      : `Rain expected ${fmt(start)} to ${fmt(end)} — plan indoor visits this afternoon.`;
  }

  // Otherwise find the best evening slot (4–9 PM Havana) with low pop + decent temp.
  const eveningCandidates = hourly
    .map((h) => ({ h, d: slotTime(h) }))
    .filter(({ d }) => d && (() => {
      const hour = parseInt(d.toLocaleString("en-US", { hour: "numeric", hour12: false, timeZone: "America/Havana" }), 10);
      return hour >= 16 && hour <= 21;
    })())
    .filter(({ h }) => (h.pop || 0) < 0.3 && h.temp_f != null);
  if (eveningCandidates.length === 0) return null;
  // Best = highest temp under 88°F, otherwise lowest pop
  const best = eveningCandidates.sort((a, b) => (a.h.pop || 0) - (b.h.pop || 0))[0];
  const fmt = (d) => d.toLocaleTimeString(lang === "es" ? "es-ES" : "en-US", { hour: "numeric", hour12: true, timeZone: "America/Havana" });
  const desc = (best.h.description || "").trim().toLowerCase();
  return lang === "es"
    ? `Mejor momento para una terraza esta noche: ${fmt(best.d)} — ${fmtTemp(best.h.temp_f, unit)}${desc ? `, ${desc}` : ""}.`
    : `Best time for a rooftop tonight: ${fmt(best.d)} — ${fmtTemp(best.h.temp_f, unit)}${desc ? ` ${desc}` : ""}.`;
}

function fmtSlotTime(slot, isNow, lang) {
  if (isNow) return null; // "Now" label is set separately by caller using t("weather_now")
  if (!slot?.dt_unix && !slot?.dt) return "—";
  const ts = slot.dt_unix ? slot.dt_unix * 1000 : Date.parse((slot.dt || "").replace(" ", "T") + "Z");
  if (Number.isNaN(ts)) return "—";
  return new Date(ts).toLocaleTimeString(lang === "es" ? "es-ES" : "en-US", {
    hour: "numeric",
    minute: undefined,
    hour12: true,
    timeZone: "America/Havana",
  });
}

export default function WeatherForecastModal({ open, onOpenChange, weather, unit, toggleUnit }) {
  const { t, lang } = useI18n();
  const c = weather?.current;
  const forecast = weather?.forecast || [];
  const hourly = weather?.hourly || [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const tips = useMemo(() => buildTips(c, forecast, t), [c, forecast, t]);
  const slots = useMemo(() => pickIntervalSlots(hourly), [hourly]);
  const planningTip = useMemo(() => buildPlanningTip(hourly, unit, t, lang), [hourly, unit, t, lang]);
  const [expandedDay, setExpandedDay] = useState(null);   // YYYY-MM-DD
  const [expandedHour, setExpandedHour] = useState(null); // index into hourly

  if (!c) return null;
  const IconCmp = ICONS[iconForCondition(c.main, c.icon)] || Sun;
  const stale = weather?.stale;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="weather-modal"
        className="border border-gold-strong p-0 max-w-lg w-[94vw] rounded-none max-h-[90vh] overflow-hidden flex flex-col"
        style={{ background: "#171717" }}
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-gold-soft">
          <DialogTitle className="font-display text-2xl text-foreground">{t("weather_havana")}</DialogTitle>
          <DialogDescription className="text-foreground/55 text-xs mt-1">
            {weather?.last_updated && (
              <span>
                {stale
                  ? <span className="text-amber-300">{t("weather_stale")} — {weather.last_updated.slice(0, 16).replace("T", " ")} UTC</span>
                  : <>{t("weather_last_updated")} {timeAgo(weather.last_updated)}</>}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* SECTION 1 — Current */}
          <section data-testid="weather-current" className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <button
                type="button"
                onClick={toggleUnit}
                data-testid="weather-current-temp"
                className="font-display text-6xl text-gold leading-none"
                aria-label="Toggle temperature unit"
              >
                {fmtTemp(c.temp_f, unit)}
              </button>
              <div className="mt-2 text-[14px] text-foreground/85">{c.description}</div>
            </div>
            <IconCmp className="w-16 h-16 text-gold shrink-0" strokeWidth={1.4} />
          </section>

          <section className="grid grid-cols-2 gap-3 text-[13px]" data-testid="weather-current-details">
            <Detail icon={Thermometer} k={t("weather_feels_like")} v={fmtTemp(c.feels_like_f, unit)} />
            <Detail icon={Droplets}    k={t("weather_humidity")}   v={c.humidity != null ? `${c.humidity}%` : "—"} />
            <Detail icon={Wind}        k={t("weather_wind")}       v={fmtWind(c.wind_mph, unit)} />
            <Detail icon={Flame}       k={t("weather_uv")}         v={c.uv_index != null ? String(c.uv_index) : "—"} />
            <Detail icon={Eye}         k={t("weather_visibility")} v={fmtDistance(c.visibility_mi, unit)} />
            <Detail icon={Sunrise}     k={t("weather_sunrise")}    v={fmtClock(c.sunrise)} />
            <Detail icon={Sunset}      k={t("weather_sunset")}     v={fmtClock(c.sunset)} />
          </section>

          {/* SECTION 2 — Tips */}
          {tips.length > 0 && (
            <section className="space-y-2" data-testid="weather-tips">
              {tips.map((tt, i) => (
                <div
                  key={i}
                  data-testid={`weather-tip-${tt.kind}`}
                  className={`px-3 py-2.5 border flex items-start gap-2 text-[13px] leading-relaxed ${
                    tt.kind === "amber" ? "border-amber-500/60 text-amber-200" :
                    tt.kind === "gold"  ? "border-gold-strong text-gold" :
                                         "border-gold-soft text-foreground/80"
                  }`}
                  style={{ background: "#1D1D1D" }}
                >
                  <tt.icon className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.6} />
                  <span>{tt.text}</span>
                </div>
              ))}
            </section>
          )}

          {/* SECTION 3 — Next 24 Hours (4 slots at ~6h intervals) */}
          {slots.length > 0 && (
            <section data-testid="weather-next-24">
              <div
                className="mb-2"
                style={{
                  color: "#E8B84B",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {t("weather_next_24")}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {slots.map((s, i) => {
                  const isNow = i === 0;
                  const SIcon = s ? (ICONS[iconForCondition(s.main, s.icon)] || Sun) : Sun;
                  const pop = s ? (s.pop || 0) * 100 : 0;
                  return (
                    <div
                      key={i}
                      data-testid={`weather-slot-${i}`}
                      className="flex flex-col items-center text-center"
                      style={{
                        background: isNow ? "rgba(232,184,75,0.12)" : "#1D1D1D",
                        border: isNow
                          ? "1px solid rgba(232,184,75,0.55)"
                          : "1px solid rgba(232,184,75,0.18)",
                        borderRadius: 10,
                        padding: "10px 4px",
                        minHeight: 92,
                      }}
                    >
                      <span
                        className="uppercase"
                        style={{
                          color: isNow ? "#E8B84B" : "#B0AAA3",
                          fontSize: 10,
                          letterSpacing: "0.15em",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        {isNow ? t("weather_now") : fmtSlotTime(s, false, lang)}
                      </span>
                      <SIcon className={`w-5 h-5 my-1 ${isNow ? "text-gold" : "text-foreground/70"}`} strokeWidth={1.6} />
                      <span
                        className="font-mono"
                        style={{
                          color: isNow ? "#E8B84B" : "#F5F1E8",
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        {s?.temp_f != null ? fmtTemp(s.temp_f, unit) : "—"}
                      </span>
                      {pop >= 10 && (
                        <span
                          className="inline-flex items-center gap-0.5 mt-0.5"
                          style={{ color: "#B0AAA3", fontSize: 10 }}
                        >
                          <CloudRain className="w-2.5 h-2.5" strokeWidth={1.5} />
                          {Math.round(pop)}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* SECTION 3.5 — Hourly scrollable row (next ~24 h in 3-hour buckets) */}
          {hourly.length > 0 && (
            <section data-testid="weather-hourly">
              <div
                className="mb-2"
                style={{
                  color: "#E8B84B",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {t("weather_hourly")}
              </div>
              <div className="overflow-x-auto no-scrollbar -mx-6 px-6">
                <div className="flex gap-2 min-w-max pb-1 items-start">
                  {hourly.map((h, i) => {
                    const HIcon = ICONS[iconForCondition(h?.main, h?.icon)] || Sun;
                    const isNow = i === 0;
                    const isExp = expandedHour === i;
                    const pop = (h?.pop || 0) * 100;
                    return (
                      <div key={i} className="flex flex-col items-stretch gap-2 shrink-0">
                        <button
                          type="button"
                          data-testid={`weather-hour-${i}`}
                          onClick={() => setExpandedHour(isExp ? null : i)}
                          aria-expanded={isExp}
                          className="flex flex-col items-center text-center transition-all"
                          style={{
                            background: isNow || isExp ? "rgba(232,184,75,0.12)" : "#1D1D1D",
                            border: isNow || isExp
                              ? "1px solid rgba(232,184,75,0.55)"
                              : "1px solid rgba(232,184,75,0.18)",
                            borderRadius: 10,
                            padding: "9px 10px",
                            minWidth: 64,
                          }}
                        >
                          <span
                            className="uppercase"
                            style={{
                              color: isNow ? "#E8B84B" : "#B0AAA3",
                              fontSize: 10,
                              letterSpacing: "0.12em",
                              fontFamily: "'DM Sans', sans-serif",
                            }}
                          >
                            {isNow ? t("weather_now") : fmtSlotTime(h, false, lang)}
                          </span>
                          <HIcon className={`w-4 h-4 my-1 ${isNow ? "text-gold" : "text-foreground/70"}`} strokeWidth={1.6} />
                          <span
                            className="font-mono"
                            style={{ color: isNow ? "#E8B84B" : "#F5F1E8", fontSize: 12, fontWeight: 500 }}
                          >
                            {h?.temp_f != null ? fmtTemp(h.temp_f, unit) : "—"}
                          </span>
                          {pop >= 10 && (
                            <span
                              className="inline-flex items-center gap-0.5 mt-0.5"
                              style={{ color: "#B0AAA3", fontSize: 9 }}
                            >
                              <CloudRain className="w-2.5 h-2.5" strokeWidth={1.5} />
                              {Math.round(pop)}%
                            </span>
                          )}
                        </button>
                        {isExp && (
                          <div
                            data-testid={`weather-hour-${i}-detail`}
                            className="text-[11px]"
                            style={{
                              background: "#171717",
                              borderLeft: "3px solid #E8B84B",
                              borderRadius: 8,
                              padding: "8px 10px",
                              color: "#D8D2BF",
                              minWidth: 158,
                              maxWidth: 220,
                              lineHeight: 1.55,
                              fontFamily: "'DM Sans', sans-serif",
                            }}
                          >
                            <KV k={t("weather_feels_like")} v={h?.temp_f != null ? fmtTemp(h.temp_f, unit) : "—"} />
                            <KV k={t("weather_rain_prob")} v={pop > 0 ? `${Math.round(pop)}%` : "—"} />
                            <KV k={t("weather_wind")} v={h?.wind_mph != null ? fmtWind(h.wind_mph, unit) : "—"} />
                            <KV k={t("weather_humidity")} v={h?.humidity != null ? `${h.humidity}%` : "—"} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {planningTip && (
                <div
                  data-testid="weather-planning-tip"
                  className="mt-3 inline-flex items-start gap-2 px-3 py-2"
                  style={{
                    background: "rgba(232,184,75,0.08)",
                    border: "1px solid rgba(232,184,75,0.35)",
                    borderRadius: 10,
                  }}
                >
                  <Sparkles className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" strokeWidth={1.6} />
                  <span style={{ color: "#E8B84B", fontSize: 13, lineHeight: 1.45, fontFamily: "'DM Sans', sans-serif" }}>
                    {planningTip}
                  </span>
                </div>
              )}
            </section>
          )}

          {/* SECTION 4 — 7-Day Forecast */}
          <section data-testid="weather-forecast">
            <div className="eyebrow mb-2">{t("weather_7day")}</div>
            <ul className="divide-y divide-[rgba(232,184,75,0.10)]">
              {forecast.map((f) => {
                const FIcon = ICONS[iconForCondition(f.main, f.icon)] || Sun;
                const today = f.date === todayIso;
                const isExp = expandedDay === f.date;
                return (
                  <li key={f.date} className="overflow-hidden">
                    <button
                      type="button"
                      data-testid={`weather-day-${f.date}`}
                      onClick={() => setExpandedDay(isExp ? null : f.date)}
                      aria-expanded={isExp}
                      className={`w-full py-2.5 flex items-center gap-3 transition-colors ${today || isExp ? "text-gold" : "text-foreground/85"}`}
                    >
                      <span className={`w-14 text-[12px] uppercase tracking-widest ${today || isExp ? "text-gold" : "text-foreground/55"}`}>
                        {dayLabel(f.date, todayIso, lang)}
                      </span>
                      <FIcon className="w-4 h-4 shrink-0" strokeWidth={1.6} />
                      <span className="text-[13px] flex-1 truncate text-left">{f.description}</span>
                      {f.pop > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-foreground/55">
                          <CloudRain className="w-3 h-3" strokeWidth={1.5} /> {popPct(f.pop)}%
                        </span>
                      )}
                      <span className="font-mono text-[13px] text-foreground">
                        {fmtTemp(f.high_f, unit)} <span className="text-foreground/45">/ {fmtTemp(f.low_f, unit)}</span>
                      </span>
                    </button>
                    {isExp && (
                      <div
                        data-testid={`weather-day-${f.date}-detail`}
                        className="pl-4 pr-2 pb-3 mt-1 mb-2 grid grid-cols-2 gap-x-4 gap-y-1.5"
                        style={{
                          borderLeft: "3px solid #E8B84B",
                          background: "rgba(232,184,75,0.04)",
                          marginLeft: 6,
                          color: "#D8D2BF",
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 12,
                          paddingTop: 8,
                        }}
                      >
                        {f.morning_f != null && <KV k={t("weather_morning")} v={fmtTemp(f.morning_f, unit)} />}
                        {f.afternoon_f != null && <KV k={t("weather_afternoon")} v={fmtTemp(f.afternoon_f, unit)} />}
                        {f.evening_f != null && <KV k={t("weather_evening")} v={fmtTemp(f.evening_f, unit)} />}
                        <KV k={t("weather_rain_prob")} v={popPct(f.pop) > 0 ? `${popPct(f.pop)}%` : "—"} />
                        {f.wind_mph != null && <KV k={t("weather_wind")} v={fmtWind(f.wind_mph, unit)} />}
                        {f.uv_index != null && <KV k={t("weather_uv")} v={String(f.uv_index)} />}
                        {f.humidity != null && <KV k={t("weather_humidity")} v={`${f.humidity}%`} />}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          {/* SECTION 5 — Unit toggle */}
          <section data-testid="weather-unit-toggle" className="pt-2 border-t border-gold-soft">
            <div className="eyebrow mb-2">{t("weather_unit")}</div>
            <div className="inline-flex gap-2">
              {[["f", "°F"], ["c", "°C"]].map(([k, l]) => {
                const active = unit === k;
                return (
                  <button
                    key={k}
                    onClick={() => { if (!active) toggleUnit(); }}
                    data-testid={`weather-unit-${k}`}
                    className={`px-5 py-3 text-sm border ${active ? "bg-gold text-[hsl(var(--primary-foreground))] border-transparent" : "border-gold-soft text-foreground/85"}`}
                    style={{ minHeight: 48, minWidth: 64 }}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ icon: Icon, k, v }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <Icon className="w-3.5 h-3.5 text-gold shrink-0" strokeWidth={1.6} />
      <span className="text-foreground/55 text-[11px] uppercase tracking-widest w-20">{k}</span>
      <span className="text-foreground font-mono">{v}</span>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span style={{ color: "#B0AAA3", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.14em" }}>{k}</span>
      <span style={{ color: "#F5F1E8", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}>{v}</span>
    </div>
  );
}
